import { createSelector } from 'reselect';
import { ObjectIdNamePair } from 'src/components/common/Types/CommonTypes';
import { isValidArray } from 'src/Library/Helper';
import masterDataProto from 'src/Library/MasterDataType';
import { getNestedProperty, setNestedProperty } from 'src/utils/utils';

const dimensionsSelector = masterData => masterData.dimensions;
const dimensionDataSelector = masterData => masterData.dimensionData;
const importMasterDataSelector = masterData => masterData.importsMaster;
const soStatusSelector = masterData => masterData.exim ? masterData.exim.soStatus : [];
const permissionSelector = masterData => masterData.permissions || {};
const carrierSelector = masterData => masterData.globalCarriers || [];
const plantToLoadingPortsMapSelector = masterData => masterData.plantToLoadingPortsMap || {};
const rfqDimensionsSelector = (masterData: any, dimensionName: string) => {
  return masterData.rfq[dimensionName] || [];
};

export const polsFromPlants = createSelector(
  [plantToLoadingPortsMapSelector],
  plantToLoadingPortsMap => Object.values(plantToLoadingPortsMap)
  .reduce((acc: ObjectIdNamePair[], itr: ObjectIdNamePair[]) => {
    acc.push(...itr);
    return acc;
  }, []),
);

export const globalShippingLines = createSelector(
  [carrierSelector],
  carriers => carriers.filter(carrier => carrier.type === 'SEA'),
);

export const globalAirlines = createSelector(
  [carrierSelector],
  carriers => carriers.filter(carrier => carrier.type === 'AIR'),
);

export const containerTrackingPermissions = createSelector(
  [permissionSelector],
  (permissions) => {
    const { CONTAINER_TRACKING_WRITE,
      CONTAINER_TRACKING_DELETE } = permissions;
    return {
      write: !!CONTAINER_TRACKING_WRITE,
      delete: !!CONTAINER_TRACKING_DELETE,
    };
  },
);

export const getPlantDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'PLANT');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getSoStatus = createSelector(
  [soStatusSelector],
  (soStatus) => {
    const optionList: any = [];
    soStatus.forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getPortDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'PORT');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getShipperDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'SHIPPER');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);
export const getShippingLineDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'SHIPPING LINE');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getAllDimensions = createSelector(
  [dimensionsSelector],
  (dimensions) => {
    return dimensions;
  },
);

export const getCHADimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    // Change it to CHA once added to masterdata
    const dataStruct = dimensions.filter(item => item.name === 'CHA');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getSurveyorDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'SURVEYOR');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    const surveyorList = dimensionDatas[dataStruct[0].id] || [];
    surveyorList.forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getBreakBulkDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    const breakBulkDimensionNames = ['BREAK BULK', 'BREAK_BULK', 'BREAKBULK'];
    const dataStruct = dimensions.find(item => breakBulkDimensionNames.includes(item.name));
    if (!dataStruct) {
      return optionList;
    }
    const breakBulkList = dimensionDatas[dataStruct.id] || [];
    breakBulkList.forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getFFDimensions = createSelector(
  [dimensionsSelector, dimensionDataSelector],
  (dimensions, dimensionDatas) => {
    const optionList: any = [];
    // Change it to CHA once added to masterdata
    const dataStruct = dimensions.filter(item => item.name === 'FREIGHT FORWARDER');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList;
  },
);

export const getplantDimensionsWithLoadingPorts = createSelector(
  [dimensionsSelector, dimensionDataSelector,
    (masterData: any) => masterData.plantToLoadingPortsMap ?
  Object.keys(masterData.plantToLoadingPortsMap) : []],
  (dimensions, dimensionDatas, plantIds) => {
    const optionList: any = [];
    const dataStruct = dimensions.filter(item => item.name === 'PLANT');
    if (!dataStruct || !dataStruct.length) {
      return optionList;
    }
    dimensionDatas[dataStruct[0].id].forEach((element) => {
      optionList.push({ key: element.id, label: element.name });
    });
    return optionList.filter(option => plantIds.indexOf(option.key) > -1);
  },
);

const getRfqUpperCaseDimensionMap = (dimensions: typeof masterDataProto.rfq.customCharges) => {
  const dimensionSorted = dimensions?.map((val) => ({
    ...val,
    key: val.code,
    label: `${val.name?.toUpperCase()} (${val.currency})`,
  }));
  dimensionSorted.sort((firstValue, secondValue) =>
    firstValue.label.localeCompare(secondValue.label)
  );
  return dimensionSorted;
};

export const getRfqDimensions = createSelector(
  [rfqDimensionsSelector],
  (dimensions: typeof masterDataProto.rfq.customCharges) => {
    const dimensionSorted = dimensions.reduce((acc, dimension) => {
      const keyPath = [
        dimension.mode,
        dimension.type,
        ...(dimension.subType ? [dimension.subType] : []),
      ];
      const prevValue = getNestedProperty(acc, keyPath);
      const valueToSet = [
        ...(isValidArray(prevValue) ? prevValue : []),
        ...getRfqUpperCaseDimensionMap([dimension]),
      ];
      setNestedProperty(acc, keyPath, valueToSet);
      return acc;
    }, {});
    return dimensionSorted;
  }
);
